import { NullOr } from "@regal-voice/shared-types";
import mapKeys from "lodash/mapKeys";
import snakeCase from "lodash/snakeCase";
import { stringify } from "qs";

import { RequestNodeMetricsData } from "Types/Journey";

import { configureServer } from "./AbstractApiService";
import { getIngestUrl } from "./ProxyConfig";

interface BaseDynamoLastEvaluatedKeyData {
    id: string;
    info: string;
}

interface ActiveUserLastEvaluatedKeyData extends BaseDynamoLastEvaluatedKeyData {
    journeyId: string;
    trackInProgressTimestamp: string;
}

export type ActiveUserLastEvaluatedKey = NullOr<ActiveUserLastEvaluatedKeyData>;

export interface UserCountsByJourney {
    journeyUuid: string;
    inProgress: number;
    finished: number;
    stopped: number;
    failed: number;
}

export interface ActiveUsersData {
    lastEvaluatedKey: ActiveUserLastEvaluatedKey;
    feed: any;
}

export interface ActiveUsersQueryParams {
    status: "in_progress"; // There are others but we dont want to query by them rn
    nodeName?: string;
    nextPageToken?: string;
}

export type TestPromptResults = {
    choice: string;
    reason: string;
    score: number;
};

const makeRequest = ({ performCaseConversion = true, performResponseCaseConversion = true } = {}) => {
    return configureServer({
        host: getIngestUrl(),
        performCaseConversion,
        performResponseCaseConversion,
    });
};

export async function journeyUserCountsByNode(journeyId: string): Promise<RequestNodeMetricsData> {
    return makeRequest()({
        endpoint: `/v1/monitoring-data/${journeyId}`,
        errors: {
            notFound: "Journeys Metric 'monitoring-data' not found",
            other: "Error retrieving data for endpoint /journeys/monitoring-data",
        },
    });
}

export async function getUserCountsByJourneysIds({
    journeysIds,
}: {
    journeysIds: string[];
}): Promise<{ journeyPageCounts: UserCountsByJourney[] }> {
    return makeRequest()({
        endpoint: `/v1/fetch-journeys-statistics`,
        errors: {
            notFound: "Journeys Metric 'journey-users-count' not found",
            other: "Error retrieving data for endpoint /journeys/fetch-journeys-statistics",
        },
        body: {
            journey_uuids: journeysIds,
        },
        retryAttempts: 1,
    });
}

export async function getActiveJourneyUsers({
    journeyId,
    params,
}: {
    journeyId: string;
    params?: ActiveUsersQueryParams;
}): Promise<ActiveUsersData> {
    const requestParams = mapKeys(params, (_, k) => snakeCase(k));
    const queryParams = stringify(requestParams, { encode: true });

    return makeRequest()({
        endpoint: `/v1/journeys-contacts/journey/${journeyId}?${queryParams}`,
        errors: {
            notFound: "Journeys endpoint 'journeys-contacts' not found",
            other: "Error retrieving data for endpoint /journeys-contacts",
        },
    });
}

export type JourneyExecutionStatus = "in_progress" | "succeeded" | "failed" | "stopped";

export const JOURNEY_EXECUTION_STATUS_LABELS: Record<JourneyExecutionStatus, string> = {
    succeeded: "Success",
    failed: "Failed",
    stopped: "Stopped",
    in_progress: "In Progress",
};

export const JOURNEY_EXECUTION_STATUS_KEYS: Record<JourneyExecutionStatus, string> = {
    succeeded: "success",
    failed: "failed",
    stopped: "stopped",
    in_progress: "inProgress",
};

export interface JourneyExecution {
    executionName: string;
    startedAt: number;
    createdAt: string;
    status: JourneyExecutionStatus;
}

export interface JourneyExecutionsResponse {
    data: JourneyExecution[];
    pageInfo: {
        cursor: NullOr<string>;
        hasNextPage: boolean;
    };
}

export interface JourneyNode {
    nodeName: string;
    nodeType: NullOr<string>;
    journeyNodeFriendlyId: NullOr<number>;
    startedAt: NullOr<number>;
    completedAt: NullOr<number>;
    stepCounter: NullOr<number>;
    nextStep: NullOr<string>;
    nodeOutput: NullOr<unknown>;
    createdAt: string;
}

export interface JourneyNodesResponse {
    data: JourneyNode[];
}

export async function getProfileJourneyExecutions({
    profileId,
    journeyUuid,
    limit,
    cursor,
}: {
    profileId: string;
    journeyUuid: string;
    limit?: number;
    cursor?: string;
}): Promise<JourneyExecutionsResponse> {
    const queryParams = stringify(
        {
            ...(limit && { limit }),
            ...(cursor && { cursor }),
        },
        { encode: true }
    );
    const query = queryParams ? `?${queryParams}` : "";

    return makeRequest()({
        endpoint: `/v1/journey-executions/${profileId}/journeys/${journeyUuid}${query}`,
        errors: {
            notFound: "Journey executions not found",
            other: "Error retrieving journey executions",
        },
    });
}

export async function getProfileJourneyExecutionNodes({
    profileId,
    journeyUuid,
    executionName,
}: {
    profileId: string;
    journeyUuid: string;
    executionName: string;
}): Promise<JourneyNodesResponse> {
    return makeRequest()({
        endpoint: `/v1/journey-executions/${profileId}/journeys/${journeyUuid}/executions/${executionName}/nodes`,
        errors: {
            notFound: "Journey execution nodes not found",
            other: "Error retrieving journey execution nodes",
        },
    });
}

export function stopJourneyExecution(requestBody: {
    journeyUuid: string;
    executionName: string;
    profileId: string;
}): Promise<any> {
    return makeRequest()({
        endpoint: "/v1/stop-single-journey-execution",
        method: "POST",
        body: requestBody,
        errors: {
            notFound: "Route not found",
            other: "Error stopping journey for user",
        },
    });
}

export function testOpenAIPrompt(requestBody: {
    prompt: string;
    choices: Array<{
        id: number;
        name: string;
        description: string;
    }>;
    model?: string;
}): Promise<TestPromptResults> {
    return makeRequest()({
        endpoint: "/v1/validate-openai-decision",
        method: "POST",
        body: requestBody,
        errors: {
            notFound: "Route not found",
            other: "Error fetching openAI response",
        },
    });
}
